import React, { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  CalendarDays,
  Trophy,
  Users,
  TrendingUp,
  Target,
} from "lucide-react";
import "./BasketballTeamPublic.css";

function BasketballTeamPublic({
  team,
  playersAttr = [],
  slug,
  navigate,
  theme,
  bannerSlot,
}) {
  const games = team?.games || [];

  const [showTeamPhoto, setShowTeamPhoto] = useState(false);
  const [showFullRoster, setShowFullRoster] = useState(false);
  const [showFullResults, setShowFullResults] = useState(false);
  /* ========================================
     GAMES
  ======================================== */

  const sortedGames = useMemo(() => {
    return [...games].sort(
      (a, b) => new Date(a?.date || 0) - new Date(b?.date || 0)
    );
  }, [games]);

  const isCompletedGame = (game) => {
    const score1 = game?.score_team1;
    const score2 = game?.score_team2;

    if (
      score1 === null ||
      score1 === undefined ||
      score2 === null ||
      score2 === undefined
    ) {
      return false;
    }

    const num1 = Number(score1);
    const num2 = Number(score2);

    if (Number.isNaN(num1) || Number.isNaN(num2)) {
      return false;
    }

    // Basketball: 0-0 = not completed yet
    if (num1 === 0 && num2 === 0) {
      return false;
    }

    return true;
  };

  const completedGames = sortedGames.filter(isCompletedGame);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcomingGames = sortedGames.filter((game) => {
    if (!game?.date) return false;
    if (isCompletedGame(game)) return false;

    const gameDate = new Date(`${game.date}T00:00:00`);
    return gameDate >= today;
  });

  const nextGame = upcomingGames[0];

  const previousGame = completedGames
    .slice()
    .sort(
      (a, b) =>
        new Date(b?.date || 0) - new Date(a?.date || 0)
    )[0];

  const featuredGame = nextGame || previousGame;
  const featuredGameType = nextGame ? "Next Game" : "Previous Game";

  /* ========================================
     TEAM HELPERS
  ======================================== */

  const getTeamInitial = () => {
    return team?.name?.charAt(0)?.toUpperCase() || "T";
  };

  const getTeamSide = (game) => {
    const currentTeamId = Number(team?.id);
    const currentSltIds = (team?.sltIds || []).map(Number);

    const team1SltId = Number(game?.team1SltId);
    const team2SltId = Number(game?.team2SltId);

    const team1Id = Number(game?.team1Id);
    const team2Id = Number(game?.team2Id);

    return {
      isTeam1:
        currentSltIds.includes(team1SltId) ||
        team1Id === currentTeamId,

      isTeam2:
        currentSltIds.includes(team2SltId) ||
        team2Id === currentTeamId,
    };
  };

  const getOpponentName = (game) => {
    if (!game) return "Opponent TBD";

    const { isTeam1, isTeam2 } = getTeamSide(game);

    const team1 = game?.team1?.trim();
    const team2 = game?.team2?.trim();

    if (!team1 && !team2) return "Opponent TBD";

    if (isTeam1) return team2 || "Opponent TBD";
    if (isTeam2) return team1 || "Opponent TBD";

    if (team1 && team2) {
      return `${team1} vs ${team2}`;
    }

    return team1 || team2 || "Opponent TBD";
  };

  const getOpponentInitial = (game) => {
    return getOpponentName(game)?.charAt(0)?.toUpperCase() || "O";
  };

  const getRecordData = () => {
    let wins = 0;
    let losses = 0;
    let pointsFor = 0;
    let pointsAgainst = 0;

    completedGames.forEach((game) => {
      const score1 = Number(game?.score_team1);
      const score2 = Number(game?.score_team2);

      if (
        Number.isNaN(score1) ||
        Number.isNaN(score2)
      ) {
        return;
      }

      const { isTeam1, isTeam2 } = getTeamSide(game);

      if (!isTeam1 && !isTeam2) return;

      const teamScore = isTeam1 ? score1 : score2;
      const opponentScore = isTeam1 ? score2 : score1;

      pointsFor += teamScore;
      pointsAgainst += opponentScore;

      if (teamScore > opponentScore) {
        wins += 1;
      }

      if (teamScore < opponentScore) {
        losses += 1;
      }
    });

    const gamesPlayed = completedGames.length;

    const avgPpg =
      gamesPlayed > 0
        ? (pointsFor / gamesPlayed).toFixed(1)
        : "0.0";

    const pointDiff = pointsFor - pointsAgainst;

    return {
      record: `${wins} - ${losses}`,
      gamesPlayed,
      avgPpg,
      pointDiff,
    };
  };

  const getResultLabel = (game) => {
    const score1 = Number(game?.score_team1);
    const score2 = Number(game?.score_team2);

    if (!isCompletedGame(game)) {
      return "Scheduled";
    }

    const { isTeam1, isTeam2 } = getTeamSide(game);

    if (!isTeam1 && !isTeam2) {
      return "Final";
    }

    const teamScore = isTeam1 ? score1 : score2;
    const opponentScore = isTeam1 ? score2 : score1;

    if (teamScore > opponentScore) return "Win";
    if (teamScore < opponentScore) return "Loss";

    return "Tie";
  };

  const formatGameResult = (game) => {
    const score1 = game?.score_team1;
    const score2 = game?.score_team2;

    if (
      score1 === null ||
      score1 === undefined ||
      score2 === null ||
      score2 === undefined
    ) {
      return "—";
    }

    return `${score1} - ${score2}`;
  };

  const formatDate = (date) => {
    if (!date) return "Date TBD";

    return new Date(`${date}T00:00:00Z`).toLocaleDateString(
      "en-US",
      {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      }
    );
  };

  const formatTime = (time) => {
    if (!time) return "";

    const [hoursRaw, minutesRaw] = String(time).split(":");

    const hours = Number(hoursRaw);
    const minutes = minutesRaw || "00";

    if (Number.isNaN(hours)) {
      return time;
    }

    const displayHour = hours % 12 || 12;
    const suffix = hours >= 12 ? "PM" : "AM";

    return `${displayHour}:${minutes} ${suffix}`;
  };

  const recordData = getRecordData();

  /* ========================================
     PLAYER / STAT HELPERS
  ======================================== */

  const getPlayerName = (player) => {
    return (
      `${player?.firstName || ""} ${player?.lastName || ""}`.trim() ||
      player?.name ||
      "Player"
    );
  };

  const getPlayerInitials = (player) => {
    const first =
      player?.firstName?.charAt(0) ||
      player?.name?.charAt(0) ||
      "";

    const last =
      player?.lastName?.charAt(0) || "";

    return `${first}${last}`.toUpperCase() || "P";
  };

  const getPlayerImage = (player) => {
    return (
      player?.image ||
      player?.image_url ||
      player?.photo ||
      player?.photo_url ||
      null
    );
  };


  const getPlayerSummary = (player) => {
    const gamesPlayed = Number(player?.gamesPlayed || 0);
    const points = Number(player?.totalPoints || 0);
    const ppg = Number(player?.ppg || 0);
    const threeMade = Number(player?.total3PM || 0);
    const ftm = Number(player?.totalFTM || 0);
    const fta = Number(player?.totalFTA || 0);

    const ftPercentage =
      player?.ftPercentage !== null &&
        player?.ftPercentage !== undefined
        ? Number(player.ftPercentage)
        : fta > 0
          ? (ftm / fta) * 100
          : 0;

    return {
      player,
      gamesPlayed,
      points,
      ppg,
      threeMade,
      ftm,
      fta,
      ftPercentage,
    };
  };

  const playerSummaries = useMemo(() => {
    return playersAttr.map(getPlayerSummary);
  }, [playersAttr]);
  const sortedRoster = playerSummaries
    .slice()
    .sort((a, b) => b.ppg - a.ppg);

  const rosterPreview = showFullRoster
    ? sortedRoster
    : sortedRoster.slice(0, 6);
  /* ========================================
     TEAM LEADERS
  ======================================== */

  const scoringLeader = playerSummaries
    .filter((item) => item.ppg > 0)
    .slice()
    .sort((a, b) => b.ppg - a.ppg)[0];

  const threePointLeader = playerSummaries
    .slice()
    .sort((a, b) => b.threeMade - a.threeMade)[0];

  const freeThrowLeader = playerSummaries
    .filter((item) => item.fta > 0)
    .slice()
    .sort(
      (a, b) =>
        b.ftPercentage - a.ftPercentage
    )[0];

  const leaders = [
    {
      key: "ppg",
      label: "Leading Scorer",
      subLabel: "Points Per Game",
      icon: Trophy,
      summary: scoringLeader,
      value: scoringLeader
        ? scoringLeader.ppg.toFixed(1)
        : "0.0",
      suffix: "PPG",
    },
    {
      key: "3pm",
      label: "3PT Leader",
      subLabel: "3-Pointers Made",
      icon: Target,
      summary: threePointLeader,
      value: threePointLeader?.threeMade || 0,
      suffix: "3PM",
    },
    {
      key: "ft",
      label: "Free Throw Leader",
      subLabel: "Free Throw Percentage",
      icon: TrendingUp,
      summary: freeThrowLeader,
      value: freeThrowLeader
        ? freeThrowLeader.ftPercentage.toFixed(1)
        : "0.0",
      suffix: "FT%",
    },
  ];

  /* ========================================
     NAVIGATION
  ======================================== */

  const goToPlayer = (playerId) => {
    if (!playerId) return;

    navigate(
      slug
        ? `/${slug}/players/${playerId}`
        : `/players/${playerId}`
    );
  };

  const goToGame = (gameId) => {
    if (!gameId) return;

    navigate(
      slug
        ? `/${slug}/games/${gameId}`
        : `/games/${gameId}`
    );
  };
  const recentResults = showFullResults
    ? completedGames.slice().reverse()
    : completedGames.slice().reverse().slice(0, 6);
  /* ========================================
     JSX
  ======================================== */

  return (
    <div
      className={`basketballTeamPublic-page basketballTeamPublic-theme-${theme || "light"
        }`}
    >
      <div className="basketballTeamPublic-inner">

        {/* =====================================
            HERO
        ===================================== */}

        <section className="basketballTeamPublic-hero">

          <div className="basketballTeamPublic-heroIdentity">



            <div className="basketballTeamPublic-titleBlock">

              <p className="basketballTeamPublic-kicker">
                {team?.sport?.type || "Basketball"}
              </p>

              <h1>
                {team?.name || "Team Name"}
              </h1>

              {(team?.captainName || team?.captain) && (
                <div className="basketballTeamPublic-captain">
                  <Users size={16} />
                  <span>
                    Captain:{" "}
                    {team?.captainName ||
                      team?.captain}
                  </span>
                </div>
              )}

              <div className="basketballTeamPublic-statGrid">

                <div className="basketballTeamPublic-statItem">
                  <Trophy size={20} />

                  <div>
                    <span>Record</span>
                    <strong>
                      {recordData.record}
                    </strong>
                  </div>
                </div>

                <div className="basketballTeamPublic-statItem">
                  <CalendarDays size={20} />

                  <div>
                    <span>Games</span>
                    <strong>
                      {recordData.gamesPlayed}
                    </strong>
                  </div>
                </div>

                <div className="basketballTeamPublic-statItem">
                  <TrendingUp size={20} />

                  <div>
                    <span>Avg PPG</span>
                    <strong>
                      {recordData.avgPpg}
                    </strong>
                  </div>
                </div>

                <div className="basketballTeamPublic-statItem">
                  <Target size={20} />

                  <div>
                    <span>Point Diff</span>

                    <strong
                      className={
                        recordData.pointDiff > 0
                          ? "positive"
                          : recordData.pointDiff < 0
                            ? "negative"
                            : ""
                      }
                    >
                      {recordData.pointDiff > 0
                        ? `+${recordData.pointDiff}`
                        : recordData.pointDiff}
                    </strong>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* NEXT GAME */}

          <aside className="basketballTeamPublic-nextCard">

            <span className="basketballTeamPublic-nextLabel">
              {featuredGameType}
            </span>

            {featuredGame ? (
              <>
                <h3>
                  vs {getOpponentName(featuredGame)}
                </h3>

                <p>
                  {formatDate(featuredGame.date)}
                  {featuredGame.time
                    ? ` • ${formatTime(
                      featuredGame.time
                    )}`
                    : ""}
                </p>

                <small>
                  {nextGame
                    ? featuredGame.location ||
                    "Location TBD"
                    : `Final: ${formatGameResult(
                      featuredGame
                    )}`}
                </small>

                <div className="basketballTeamPublic-matchup">

                  <div className="basketballTeamPublic-matchupTeam">
                    <div className="basketballTeamPublic-matchupCircle basketballTeamPublic-matchupCircle-home">
                      {getTeamInitial()}
                    </div>

                    <strong>
                      {team?.name
                        ?.slice(0, 3)
                        ?.toUpperCase() || "TEAM"}
                    </strong>
                  </div>

                  <span className="basketballTeamPublic-vs">
                    vs
                  </span>

                  <div className="basketballTeamPublic-matchupTeam">
                    <div className="basketballTeamPublic-matchupCircle basketballTeamPublic-matchupCircle-away">
                      {getOpponentInitial(
                        featuredGame
                      )}
                    </div>

                    <strong>
                      {getOpponentName(
                        featuredGame
                      )
                        ?.slice(0, 3)
                        ?.toUpperCase()}
                    </strong>
                  </div>

                </div>

                <button
                  type="button"
                  onClick={() =>
                    goToGame(featuredGame.id)
                  }
                >
                  View Game Details
                </button>
              </>
            ) : (
              <div className="basketballTeamPublic-noGame">
                <h3>No upcoming game</h3>
                <p>
                  Schedule has not been added yet.
                </p>
              </div>
            )}

          </aside>
        </section>

        {/* =====================================
            TEAM PHOTO
        ===================================== */}

        {team?.logo && (
          <>
            <section className="basketballTeamPublic-teamPhotoCard">
              <img
                src={team.logo}
                alt={`${team?.name || "Team"} team`}
                className="basketballTeamPublic-teamPhoto"
                onClick={() =>
                  setShowTeamPhoto(true)
                }
                onLoad={(e) => {
                  console.log(
                    "IMAGE NATURAL:",
                    e.currentTarget.naturalWidth,
                    e.currentTarget.naturalHeight
                  );

                  console.log(
                    "IMAGE DISPLAYED:",
                    e.currentTarget.clientWidth,
                    e.currentTarget.clientHeight
                  );
                }}
              />
            </section>

            {showTeamPhoto && (
              <div
                className="basketballTeamPublic-photoModal"
                onClick={() =>
                  setShowTeamPhoto(false)
                }
              >
                <button
                  type="button"
                  className="basketballTeamPublic-photoClose"
                  onClick={() =>
                    setShowTeamPhoto(false)
                  }
                  aria-label="Close team photo"
                >
                  ×
                </button>

                <img
                  src={team.logo}
                  alt={`${team?.name || "Team"} team`}
                  className="basketballTeamPublic-photoModalImage"
                  onClick={(e) =>
                    e.stopPropagation()
                  }
                />
              </div>
            )}
          </>
        )}

        {bannerSlot && (
          <div className="basketballTeamPublic-bannerWrap">
            {bannerSlot}
          </div>
        )}

        {/* =====================================
            MAIN 3-COLUMN AREA
        ===================================== */}

        <section className="basketballTeamPublic-dashboardGrid">

          {/* ROSTER PREVIEW */}

          <div className="basketballTeamPublic-card">

            <div className="basketballTeamPublic-cardHeader">
              <h2>Roster Preview</h2>

              <span>
                {playersAttr.length}{" "}
                {playersAttr.length === 1
                  ? "Player"
                  : "Players"}
              </span>
            </div>

            {rosterPreview.length > 0 ? (
              <>
                <div className="basketballTeamPublic-rosterHeader">
                  <span>Player</span>
                  <span>GP</span>
                  <span>PPG</span>
                </div>

                <div className="basketballTeamPublic-rosterPreview">

                  {rosterPreview.map((summary) => {
                    const player = summary.player;

                    return (
                      <NavLink
                        key={
                          player?.roster_id ||
                          player?.id
                        }
                        to={
                          slug
                            ? `/${slug}/players/${player.id}`
                            : `/players/${player.id}`
                        }
                        className="basketballTeamPublic-rosterPreviewRow"
                      >


                        <div className="basketballTeamPublic-rosterPlayer">

                          {getPlayerImage(player) ? (
                            <img
                              src={getPlayerImage(player)}
                              alt={getPlayerName(
                                player
                              )}
                            />
                          ) : (
                            <div className="basketballTeamPublic-playerAvatarFallback">
                              {getPlayerInitials(
                                player
                              )}
                            </div>
                          )}

                          <strong>
                            {getPlayerName(player)}
                          </strong>
                        </div>

                        <span>
                          {summary.gamesPlayed ?? "—"}
                        </span>

                        <strong>
                          {summary.ppg.toFixed(1)}
                        </strong>
                      </NavLink>
                    );
                  })}

                </div>
                {playersAttr.length > 6 && (
                  <div className="basketballTeamPublic-cardFooter">
                    <span>
                      {showFullRoster
                        ? `Showing all ${playersAttr.length} players`
                        : `Showing 6 of ${playersAttr.length} players`}
                    </span>

                    <button
                      type="button"
                      onClick={() => setShowFullRoster((prev) => !prev)}
                    >
                      {showFullRoster ? "Show Less" : "View All Roster"}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="basketballTeamPublic-empty">
                No players added yet.
              </div>
            )}

          </div>

          {/* RECENT RESULTS */}

          <div className="basketballTeamPublic-card">

            <div className="basketballTeamPublic-cardHeader">
              <h2>Recent Results</h2>
            </div>

            {completedGames.length > 0 ? (
              <div className="basketballTeamPublic-results">

                <div className="basketballTeamPublic-resultsHeader">
                  <span>Date</span>
                  <span>Opponent</span>
                  <span>Result</span>
                  <span>Score</span>
                </div>

                {recentResults.map((game) => {
                    const result =
                      getResultLabel(game);

                    return (
                      <button
                        type="button"
                        key={game.id}
                        className="basketballTeamPublic-resultRow"
                        onClick={() =>
                          goToGame(game.id)
                        }
                      >
                        <span>
                          {formatDate(game.date)}
                        </span>

                        <strong>
                          vs{" "}
                          {getOpponentName(game)}
                        </strong>

                        <span
                          className={`basketballTeamPublic-result basketballTeamPublic-result-${result.toLowerCase()}`}
                        >
                          {result}
                        </span>

                        <strong>
                          {formatGameResult(game)}
                        </strong>
                      </button>
                    );
                  })}
              </div>
            ) : (
              <div className="basketballTeamPublic-empty">
                No completed games yet.
              </div>
            )}
            {completedGames.length > 6 && (
              <div className="basketballTeamPublic-cardFooter">
                <span>
                  {showFullResults
                    ? `Showing all ${completedGames.length} results`
                    : `Showing 6 of ${completedGames.length} results`}
                </span>

                <button
                  type="button"
                  onClick={() => setShowFullResults((prev) => !prev)}
                >
                  {showFullResults ? "Show Less" : "View All Results"}
                </button>
              </div>
            )}
          </div>

          {/* TEAM LEADERS */}

          <div className="basketballTeamPublic-card">

            <div className="basketballTeamPublic-cardHeader">
              <h2>Team Leaders</h2>
            </div>

            <div className="basketballTeamPublic-leaders">

              {leaders.map((leader) => {
                const LeaderIcon = leader.icon;
                const player =
                  leader.summary?.player;

                return (
                  <button
                    type="button"
                    key={leader.key}
                    className="basketballTeamPublic-leaderRow"
                    onClick={() =>
                      player?.id &&
                      goToPlayer(player.id)
                    }
                    disabled={!player?.id}
                  >
                    <div className="basketballTeamPublic-leaderIcon">
                      <LeaderIcon size={21} />
                    </div>

                    <div className="basketballTeamPublic-leaderType">
                      <strong>
                        {leader.label}
                      </strong>

                      <span>
                        {leader.subLabel}
                      </span>
                    </div>

                    <div className="basketballTeamPublic-leaderPlayer">

                      {player &&
                        getPlayerImage(player) ? (
                        <img
                          src={getPlayerImage(player)}
                          alt={getPlayerName(
                            player
                          )}
                        />
                      ) : (
                        <div className="basketballTeamPublic-playerAvatarFallback">
                          {player
                            ? getPlayerInitials(
                              player
                            )
                            : "—"}
                        </div>
                      )}

                      <strong>
                        {player
                          ? getPlayerName(player)
                          : "No stats yet"}
                      </strong>
                    </div>

                    <div className="basketballTeamPublic-leaderValue">
                      <strong>
                        {leader.value}
                      </strong>

                      <span>
                        {leader.suffix}
                      </span>
                    </div>
                  </button>
                );
              })}

            </div>
          </div>

        </section>
      </div>
    </div>
  );
}

export default BasketballTeamPublic;